import React, { useState, useRef, useEffect } from 'react';
import { Mic, Upload, AlertTriangle, Loader2, Play } from 'lucide-react';
import Tooltip from './Tooltip';
import { logEnhancedGeneration } from '../utils/enhancedAnalytics';
import { collection, addDoc, doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { checkAndUpdateQuota, getQuotaStatus } from '../utils/quotaManager';

// Credits per minute of audio
const CREDITS_PER_MINUTE = 500;
// Maximum audio duration in seconds
const MAX_AUDIO_DURATION = 4 * 60; // 4 minutes

// Pulsing record button animation
const PulsingRecordButton = ({ isRecording, onClick, disabled }) => {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="relative flex items-center justify-center"
    >
      {/* Outer animated pulse ring (conditionally rendered) */}
      {isRecording && (
        <span className="absolute w-24 h-24 rounded-full bg-red-500 opacity-20 animate-ping"></span>
      )}
      
      {/* Base circle */}
      <div className={`relative flex items-center justify-center w-20 h-20 rounded-full transition-colors ${isRecording ? 'bg-red-600' : 'bg-[#178A94] hover:bg-[#146E75]'}`}>
        {/* Icon */}
        <Mic 
          size={32} 
          className={`text-white transition-all ${isRecording ? 'animate-pulse' : ''}`}
        />
      </div>
    </button>
  );
};

const SpeechDenoiser = ({ 
  user = {},
  setQuotaStatus = () => {},
  onAudioGenerated = () => {},
  onClose = null
}) => {
  // Audio recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);

  // File upload state
  const [uploadedFile, setUploadedFile] = useState(null);

  // Input audio state (either recorded or uploaded)
  const [inputAudio, setInputAudio] = useState(null);
  const [inputAudioDuration, setInputAudioDuration] = useState(0);
  const [inputAudioName, setInputAudioName] = useState('');
  const [inputAudioUrl, setInputAudioUrl] = useState(null);

  // Processing state
  const [isProcessing, setIsProcessing] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Refs for recording
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const recordingStartTimeRef = useRef(null); // Use ref instead of state for immediate access
  const fileInputRef = useRef(null);
  const uploadAreaRef = useRef(null);

  // Clean up URLs when component unmounts
  useEffect(() => {
    return () => {
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
    };
  }, [inputAudioUrl]);

  // Handle recording start
  const startRecording = async () => {
    try {
      // If there's an active recording, stop it first
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
        mediaRecorderRef.current.stop();
        clearInterval(recordingTimerRef.current);

        // Give the recorder time to process the stop event
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Reset recording state completely
      audioChunksRef.current = [];
      setRecordingDuration(0);

      // Close any playing audio if a new recording starts
      if (onClose && inputAudio) {
        onClose();
      }

      // Clear all previous audio data
      setInputAudio(null);
      setInputAudioName('');
      if (inputAudioUrl) {
        URL.revokeObjectURL(inputAudioUrl);
      }
      setInputAudioUrl(null);
      setUploadedFile(null);

      // Setup MediaRecorder with 16kHz audio
      const options = {
        mimeType: 'audio/webm'
      };

      mediaRecorderRef.current = new MediaRecorder(stream, options);

      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      });

      // Store the Firebase timestamp when recording starts directly in the ref for immediate access
      recordingStartTimeRef.current = Timestamp.now();
      
      mediaRecorderRef.current.addEventListener('stop', () => {
        // Convert WebM to MP3 for better browser support
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });

        // Create a File from the Blob to better handle the data
        const currentDate = new Date();
        const fileName = `recording-${currentDate.toISOString()}.webm`;
        const audioFile = new File([audioBlob], fileName, { type: 'audio/webm' });

        // Create URL for playback
        const url = URL.createObjectURL(audioBlob);

        // Calculate accurate duration using Firebase timestamps via the ref
        const endTime = Timestamp.now();
        let accurateDuration = 0;

        if (recordingStartTimeRef.current) {
          // Get seconds difference between timestamps
          accurateDuration = endTime.seconds - recordingStartTimeRef.current.seconds;

          // Account for nanoseconds difference
          const nanoSecDiff = endTime.nanoseconds - recordingStartTimeRef.current.nanoseconds;
          const nanoSecAdjustment = nanoSecDiff / 1000000000; // Convert to seconds

          accurateDuration += nanoSecAdjustment;

          // Duration calculated accurately from timestamps
        } else {
          // No recording start time available, falling back to JS timer
        }

        // Ensure duration is reasonable - never less than 1 second
        const finalDuration = (accurateDuration > 0) ? Math.max(Math.ceil(accurateDuration), 1) :
                             (recordingDuration > 0 ? recordingDuration : 1);

        // Reset the timestamp ref for next recording
        recordingStartTimeRef.current = null;

        // Set the input audio data for recorded audio
        setInputAudio(audioFile);
        setInputAudioDuration(finalDuration);
        setInputAudioName('Recorded Audio');
        setInputAudioUrl(url);

        // Stop all tracks from the stream
        stream.getTracks().forEach(track => track.stop());
      });
      
      // Start recording
      mediaRecorderRef.current.start();
      setIsRecording(true);
      
      // Start duration timer
      let startTime = Date.now();
      recordingTimerRef.current = setInterval(() => {
        const currentDuration = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(currentDuration);
        
        // Stop recording if it exceeds the maximum duration
        if (currentDuration >= MAX_AUDIO_DURATION) {
          stopRecording();
        }
      }, 1000);
    } catch (error) {
      console.error('Failed to start recording:', error);
      alert('Could not access microphone. Please allow microphone access or try a different browser.');
    }
  };

  // Handle recording stop
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null; // Clear the timer reference
    }
  };
  
  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  // Handle drop event
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files[0]);
    }
  };
  
  // Handle file input change
  const handleFileUpload = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files[0]);
    }
  };
  
  // Process the uploaded file
  const handleFiles = (file) => {
    if (!file) return;

    // Check if the file is a valid audio type (WAV or MP3)
    if (!file.type.match('audio/wav') && !file.type.match('audio/mpeg')) {
      alert('Please upload a WAV or MP3 file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    // Close any playing audio if a new file is uploaded
    if (onClose && inputAudio) {
      onClose();
    }

    // Create URL for the uploaded file
    const fileUrl = URL.createObjectURL(file);

    // Get file duration
    const audio = new Audio();
    audio.src = fileUrl;

    audio.addEventListener('loadedmetadata', () => {
      const duration = Math.floor(audio.duration);

      // Check if audio exceeds maximum duration
      if (duration > MAX_AUDIO_DURATION) {
        alert(`Audio exceeds the maximum duration of ${MAX_AUDIO_DURATION / 60} minutes. Please upload a shorter audio file.`);
        URL.revokeObjectURL(fileUrl);
        setUploadedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        return;
      }

      setInputAudioDuration(duration);
      setUploadedFile(file);
      setInputAudio(file);
      setInputAudioName(file.name);
      setInputAudioUrl(fileUrl);
    });
    
    audio.addEventListener('error', () => {
      alert('Failed to load audio file. Please try a different file.');
      URL.revokeObjectURL(fileUrl);
      setUploadedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    });
  };
  
  // Play audio using GlobalAudioPlayer
  const handlePlayAudio = () => {
    if (!inputAudioUrl) return;

    const audioTitle = uploadedFile ? uploadedFile.name : 'Recorded Audio';
    const previewAudio = {
      url: inputAudioUrl,
      title: `Input: ${audioTitle}`,
      type: 'input-audio',
      id: 'input-' + Date.now()
    };
    onAudioGenerated(previewAudio);
  };

  // Calculate credits required for denoising
  const calculateCreditsRequired = (durationSecs) => {
    if (!durationSecs || isNaN(durationSecs) || durationSecs <= 0) {
      return 0;
    }
    const durationMins = durationSecs / 60;
    return Math.ceil(durationMins * CREDITS_PER_MINUTE);
  };

  // Handle audio denoising
  const handleDenoise = async () => {
    if (!inputAudio || isProcessing) return;

    // Calculate credits required
    const creditsRequired = calculateCreditsRequired(inputAudioDuration);

    // Check if user has enough credits
    try {
      // Check if user object is properly defined
      if (!user || !user.uid) {
        alert('User information is missing. Please log in again.');
        return;
      }

      // Wrap quota check in try-catch to handle errors specifically
      let quotaCheck;
      try {
        quotaCheck = await checkAndUpdateQuota(user.uid, creditsRequired);
      } catch (quotaError) {
        console.error("Error checking quota:", quotaError);
        alert('Failed to check quota. Please try again.');
        return;
      }

      if (!quotaCheck.canProceed) {
        alert(`You don't have enough credits. Required: ${creditsRequired.toLocaleString()}, Available: ${quotaCheck.remainingChars.toLocaleString()}`);
        return;
      }

      setIsProcessing(true);

      try {
        // Step 1: Get a blob URL from Azure function
        // Get blob URL for upload
        const blobResponse = await fetch(
          "https://voiceops.azurewebsites.net/api/createblobobject?code=TG1_oEhJKuhbzFkJuRnUllLE8L6UdkzFBnU6Vp535PSpAzFuVq_xpA==",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              userId: user.uid,
              format: uploadedFile ? uploadedFile.type.split('/')[1] : 'webm'
            })
          }
        );

        if (!blobResponse.ok) {
          throw new Error(`Failed to get blob upload URL: ${blobResponse.status} ${blobResponse.statusText}`);
        }

        const blobData = await blobResponse.json();
        const { blobUrl, blobName } = blobData;

        if (!blobUrl || !blobName) {
          throw new Error('Invalid blob upload URL received');
        }

        // Step 2: Upload audio file to the blob URL
        const uploadResponse = await fetch(blobUrl, {
          method: 'PUT',
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'Content-Type': uploadedFile ? uploadedFile.type : 'audio/webm'
          },
          body: inputAudio
        });

        if (!uploadResponse.ok) {
          throw new Error(`Failed to upload audio to blob: ${uploadResponse.status} ${uploadResponse.statusText}`);
        }

        // Step 3: Call denoiser Azure function with the blob URL
        const denoiserResponse = await fetch(
          "https://consumelucidapp.azurewebsites.net/api/Denoiserhandler?code=2Ti2BeHEMvhp8CAj6d6VAfoMPM1i7QDyUy8xatrdFK81AzFuW4nPMQ==",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              uploader_url: `https://luciddenoised.blob.core.windows.net/input/${blobName}`
            })
          }
        );

        if (!denoiserResponse.ok) {
          throw new Error(`Denoising failed: ${denoiserResponse.status} ${denoiserResponse.statusText}`);
        }

        const denoiserData = await denoiserResponse.json();

        if (!denoiserData.dl_url) {
          throw new Error('No denoised audio URL returned');
        }

        // Create audio object with metadata for the player
        const audioData = {
          url: denoiserData.dl_url,
          title: `Denoised: ${inputAudioName}`,
          type: 'denoised',
          id: 'denoised-' + Date.now()
        };

        // Pass the generated audio back to parent
        onAudioGenerated(audioData);

        // Log the successful generation
        if (user && user.uid && user.email) {
          // Log to regular generation logs for backward compatibility
          await logEnhancedGeneration(
            user.uid,
            user.email,
            'denoiser', // No voice ID for denoiser
            creditsRequired,
            'Speech Denoiser',
            denoiserData.dl_url,
            0, // No similarity value for denoiser
            0, // No pace value for denoiser
            "success" // success string instead of boolean
          );

          // Log to dedicated denoiser logs with more detailed information
          try {
            // Use setDoc with auto-generated ID to avoid permission issues
            const logRef = doc(collection(db, 'denoiser_logs'));
            await setDoc(logRef, {
              userId: user.uid,
              userEmail: user.email || 'anonymous@user.com', // Provide fallback
              inputDuration: inputAudioDuration || 0,
              inputType: uploadedFile ? 'uploaded' : 'recorded',
              inputFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
              blobName: blobName,
              inputBlobUrl: `https://luciddenoised.blob.core.windows.net/input/${blobName}`,
              creditsUsed: creditsRequired,
              outputUrl: denoiserData.dl_url,
              timestamp: serverTimestamp(),
              success: "success", // Use string instead of boolean
              audioSource: uploadedFile ? uploadedFile.name : 'Recording',
              audioSize: inputAudio.size || 0,
              requestDetails: {
                denoiseRequest: true,
                requestTimestamp: serverTimestamp(),
                processingTimeStart: Date.now(),
                audioDuration: inputAudioDuration,
                outputFormat: "audio/mp3",
                noiseReductionApplied: true
              }
            });
          } catch (logError) {
            console.error("Error logging to denoiser_logs collection:", logError);
            // Don't alert the user since denoising was still successful
          }

          // Update quota display after successful denoising
          try {
            if (setQuotaStatus) {
              const newStatus = await getQuotaStatus(user.uid);
              setQuotaStatus(newStatus);
            }
          } catch (quotaError) {
            console.error("Error updating quota display:", quotaError);
            // Don't show error to user since the denoising was still successful
          }
        }

      } catch (error) {
        console.error('Denoising failed:', error);
        if (user && user.uid && user.email) {
          // Log to regular generation logs
          await logEnhancedGeneration(
            user.uid,
            user.email,
            'denoiser',
            creditsRequired,
            'Speech Denoiser',
            null, // no output URL for failed attempts
            0, // No similarity value for denoiser
            0, // No pace value for denoiser
            "failed" // failed string instead of boolean
          );

          // Log to dedicated denoiser logs
          try {
            // Use setDoc with auto-generated ID to avoid permission issues
            const logRef = doc(collection(db, 'denoiser_logs'));
            await setDoc(logRef, {
              userId: user.uid,
              userEmail: user.email || 'anonymous@user.com', // Provide fallback
              inputDuration: inputAudioDuration || 0,
              inputType: uploadedFile ? 'uploaded' : 'recorded',
              inputFormat: uploadedFile ? uploadedFile.type : 'audio/webm',
              inputBlobUrl: null,
              creditsUsed: creditsRequired,
              error: error.message || 'Unknown error',
              timestamp: serverTimestamp(),
              success: "failed",
              audioSource: uploadedFile ? uploadedFile.name : 'Recording',
              audioSize: inputAudio ? inputAudio.size : 0,
              requestDetails: {
                denoiseRequest: true,
                requestTimestamp: serverTimestamp(),
                processingTimeStart: Date.now(),
                audioDuration: inputAudioDuration,
                errorDetails: error.toString(),
                processingFailed: true
              }
            });
          } catch (logError) {
            console.error("Error logging to denoiser_logs collection:", logError);
            // Continue to show the main error to the user
          }
        }
        alert(`Failed to denoise audio: ${error.message || 'Unknown error'}. Please try again.`);
      } finally {
        setIsProcessing(false);
      }
    } catch (outerError) {
      console.error('Unexpected error in denoising process:', outerError);
      alert('An unexpected error occurred. Please try again.');
      setIsProcessing(false);
    }
  };
  
  // Format time for display
  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) {
      return '0:00';
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  // Determine if we have a recorded or uploaded audio
  const isRecordedAudio = inputAudio && !uploadedFile;
  const isUploadedAudio = inputAudio && uploadedFile;

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="grid grid-cols-12 gap-[1rem]">
        {/* Main Content Column */}
        <div className="col-span-12 bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-[1.25rem] h-[clamp(500px,75vh,900px)] flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-bold text-[#EAECEF]">Speech Denoiser</div>

            {/* Tooltip on the Far Right */}
            <div className="flex items-center">
              <Tooltip 
                text={[
                  "Upload or record audio up to 4 minutes long.",
                  "Only WAV and MP3 files are supported for upload.",
                  "Remove background noise from your audio.",
                  "500 credits are required per minute of audio denoising."
                ]}
              />
            </div>
          </div>

          {/* Main working area - 80% of height */}
          <div className="flex-grow relative bg-[#181A1B] rounded-xl overflow-hidden mb-5 border border-[#3A3D3E]">
            {/* Vertical divider */}
            <div className="absolute left-1/2 top-4 bottom-4 border-l border-dashed border-[#178A94] opacity-60 transform -translate-x-1/2"></div>
            
            {/* Left side - Recording */}
            <div className="absolute left-0 top-0 w-1/2 h-full flex items-center justify-center">
              <div className="flex flex-col items-center">
                <PulsingRecordButton 
                  isRecording={isRecording} 
                  onClick={isRecording ? stopRecording : startRecording}
                  disabled={isProcessing}
                />
                <div className="mt-4 text-[#B0B4BB]">
                  {isRecording ? `Recording... ${formatTime(recordingDuration)}` : 'Tap to record'}
                </div>
              </div>
            </div>
            
            {/* Right side - File Upload */}
            <div className="absolute right-0 top-0 w-1/2 h-full">
              <div 
                ref={uploadAreaRef}
                className={`h-full flex flex-col items-center justify-center p-6 transition-colors
                          ${dragActive ? 'bg-[#178A94]/20' : 'hover:bg-[#2C2F30]/30'}
                          ${isProcessing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => !isProcessing && !isRecording && fileInputRef.current.click()}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className={`p-4 rounded-full mb-3 transition-colors
                               ${dragActive ? 'bg-[#178A94]/40' : 'bg-[#2C2F30]'}`}>
                  <Upload size={28} className="text-[#178A94]" />
                </div>
                <div className="text-center">
                  <p className="text-[#EAECEF] mb-1">Drop audio file here</p>
                  <p className="text-[#B0B4BB] text-sm">or click to browse</p>
                  <p className="text-[#7C8085] text-xs mt-3">Supported: .wav, .mp3</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  accept="audio/wav,audio/mpeg"
                  onChange={handleFileUpload}
                  disabled={isProcessing || isRecording}
                />
              </div>
            </div>
          </div>
          
          {/* Audio Status - 15% of height */}
          {inputAudio && (
            <div className="bg-[#2C2F30] rounded-lg border border-[#3A3D3E] p-3 mb-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0 mr-2">
                  <div className="mr-2 flex-shrink-0 py-1 px-2 bg-[#178A94]/20 rounded text-[#178A94] text-xs font-medium uppercase">
                    Ready
                  </div>
                  <div className="text-[#EAECEF] font-medium truncate min-w-0 flex-1">
                    {inputAudioName}
                  </div>
                </div>

                <div className="flex items-center gap-3 flex-shrink-0">
                  {/* Show duration for all audio types */}
                  {inputAudioDuration > 0 && (
                    <div className="text-[#B0B4BB] text-sm">
                      {formatTime(inputAudioDuration)}
                    </div>
                  )}

                  {/* Play button for all audio types */}
                  <button
                    onClick={handlePlayAudio}
                    className="p-2 bg-[#178A94] rounded-full hover:bg-[#146E75] transition-colors"
                  >
                    <Play size={14} className="text-white" />
                  </button>
                </div>
              </div>

              {/* Show credits estimate for all audio with known duration */}
              {inputAudioDuration > 0 && (
                <div className="mt-2 text-xs text-center text-[#B0B4BB]">
                  Est. credits: {calculateCreditsRequired(inputAudioDuration).toLocaleString()}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    
      {/* Denoise Button */}
      <div className="w-full">
        <button
          onClick={handleDenoise}
          disabled={!inputAudio || isProcessing}
          className="w-full p-[0.9rem] sm:p-[1.15rem] bg-[#178A94] text-[#EAECEF] rounded-lg uppercase font-bold
                  hover:bg-[#146E75] transition-colors
                  disabled:opacity-50 disabled:cursor-not-allowed
                  flex items-center justify-center text-[clamp(0.9rem,1.5vw,1.125rem)]
                  mb-[4rem] sm:mb-[2rem] pb-[1.2rem] sm:pb-[1.15rem]"
        >
          {isProcessing ? (
            <>
              <Loader2 className="animate-spin mr-[0.5rem]" size={20} />
              Processing...
            </>
          ) : (
            <>
              <span className="mr-[0.5rem] text-xl">⚡</span>
              Remove Noise
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default SpeechDenoiser;